/**
 * TopShelf HYDROS bundle configurator.
 *
 * Compatibility is decided server-side and delivered in the section's JSON config:
 * each accessory carries the controller keys it fits, the ports it consumes, and a
 * plain-language note. This element only filters, totals, and submits — it never
 * infers compatibility on its own.
 */
(() => {
  const PORT_LABELS = {
    outlets: 'AC outlets',
    sense: 'Sense Ports',
    drive: 'Drive Ports',
    dosing: 'Dosing heads',
    probe: 'pH / ORP ports',
    salinity: 'Salinity port',
    force: 'Force Ports',
    analog: '0–10V inputs',
  };
  const PORT_ORDER = ['sense', 'drive', 'probe', 'salinity', 'analog', 'force', 'dosing', 'outlets'];
  const GROUP_LABELS = {
    sensors_safety: 'Sensors & safety',
    dosing_ato: 'Dosing & ATO',
    power_connectivity: 'Power & connectivity',
    flow_control: 'Flow control',
  };
  // Keep in sync with the motion selectors in topshelf-hydros-bundle-configurator.css.
  const REVEAL_SELECTOR =
    '.tshbc__step-head, .tshbc__selected, .tshbc__group-title, .tshbc__toolbar-end, .tshbc__summary, .tshbc__guide';
  const CASCADE_SELECTOR = '.tshbc__controllers, .tshbc-expansion, .tshbc__filters, .tshbc__grid';

  class TopShelfHydrosConfigurator extends HTMLElement {
    constructor() {
      super();
      this.selections = new Map();
      this.expansions = new Map();
      this.controllerKey = null;
      this.busy = false;
      this.controller = null;
      this.inViewport = true;
      this.hasPortOverload = false;
      this.lastControllerKey = null;
      this.renderedLineKeys = null;
      this.renderedExpansionKeys = null;
    }

    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.controller = new AbortController();
      this.enhanceGuide();
      this.setupMotion();

      this.config = this.readConfig();
      if (!this.config) return;

      this.storageKey = this.dataset.storageKey || 'topshelf-hydros-build';
      this.money = new Intl.NumberFormat(this.config.locale || undefined, {
        style: 'currency',
        currency: this.config.currency || 'USD',
      });

      this.cache();
      this.restore();

      this.addEventListener('click', this.onClick, { signal: this.controller.signal });
      this.addEventListener('change', this.onChange, { signal: this.controller.signal });
      this.addEventListener('keydown', this.onKeydown, { signal: this.controller.signal });

      if ('IntersectionObserver' in window) {
        this.viewportObserver = new IntersectionObserver(([entry]) => {
          this.inViewport = entry.isIntersecting;
          this.updateDockVisibility();
        });
        this.viewportObserver.observe(this);
      }

      if (!this.controllerKey) {
        const first = this.controllerCards[0];
        this.controllerKey = first ? first.dataset.key : null;
      }
      this.selectController(this.controllerKey, { silent: true });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.viewportObserver?.disconnect();
      this.revealObserver?.disconnect();
      this.dataset.ready = 'false';
    }

    setupMotion() {
      if (!('IntersectionObserver' in window)) {
        this.dataset.motion = 'off';
        return;
      }

      this.querySelectorAll(CASCADE_SELECTOR).forEach((container) => {
        Array.from(container.children)
          .filter((child) => !child.hidden)
          .forEach((child, index) => child.style.setProperty('--tshbc-i', String(index)));
      });

      this.revealObserver?.disconnect();
      this.revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-revealed');
            this.revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -6% 0px' }
      );
      this.querySelectorAll(`${REVEAL_SELECTOR}, ${CASCADE_SELECTOR}`).forEach((node) => {
        if (!node.classList.contains('is-revealed')) this.revealObserver.observe(node);
      });
      this.dataset.motion = 'ready';
    }

    replayEntrance(node) {
      if (!node) return;
      node.classList.remove('is-entering');
      void node.offsetWidth;
      node.classList.add('is-entering');
    }

    enhanceGuide() {
      const guide = this.querySelector('.tshbc__guide');
      if (!guide) return;

      if (guide.dataset.mobileSections !== 'true') {
        const children = Array.from(guide.children);
        if (!children.some((child) => child.tagName === 'H2')) return;

        const fragment = document.createDocumentFragment();
        const intro = document.createElement('div');
        intro.className = 'tshbc-guide-intro';
        fragment.append(intro);

        let sectionContent = null;
        let sectionIndex = 0;

        children.forEach((child) => {
          if (child.tagName === 'H2') {
            const details = document.createElement('details');
            details.className = 'tshbc-guide-section';
            details.open = sectionIndex === 0;

            const summary = document.createElement('summary');
            summary.className = 'tshbc-guide-section__summary';

            child.classList.add('tshbc-guide-section__title');

            const toggle = document.createElement('span');
            toggle.className = 'tshbc-guide-section__toggle';
            toggle.setAttribute('aria-hidden', 'true');
            toggle.textContent = '+';

            sectionContent = document.createElement('div');
            sectionContent.className = 'tshbc-guide-section__content';

            summary.append(child, toggle);
            details.append(summary, sectionContent);
            fragment.append(details);
            sectionIndex += 1;
            return;
          }

          (sectionContent || intro).append(child);
        });

        if (!intro.childElementCount) intro.remove();
        guide.replaceChildren(fragment);
        guide.dataset.mobileSections = 'true';
      }

      this.guideSections = Array.from(guide.querySelectorAll('.tshbc-guide-section'));
      this.guideMedia = window.matchMedia('(max-width: 749px)');

      const syncGuideSections = ({ matches }) => {
        this.guideSections.forEach((section, index) => {
          section.open = !matches || index === 0;
        });
      };

      syncGuideSections(this.guideMedia);
      this.guideMedia.addEventListener('change', syncGuideSections, {
        signal: this.controller.signal,
      });
    }

    readConfig() {
      const node = this.querySelector('[data-config]');
      if (!node) return null;
      try {
        return JSON.parse(node.textContent || '{}');
      } catch (error) {
        console.error('HYDROS configurator: invalid config.', error);
        return null;
      }
    }

    cache() {
      this.controllerCards = Array.from(this.querySelectorAll('[data-controller-card]'));
      this.accessoryCards = Array.from(this.querySelectorAll('[data-accessory]'));
      this.groupSections = Array.from(this.querySelectorAll('[data-group-section]'));
      this.filterButtons = Array.from(this.querySelectorAll('[data-filter]'));
      this.recommendedToggle = this.querySelector('[data-recommended-toggle]');
      this.expansionPicker = this.querySelector('[data-expansion-picker]');
      this.expansionAddButton = this.querySelector('[data-add-expansion]');
      this.expansionList = this.querySelector('[data-expansion-list]');
      this.expansionEmpty = this.querySelector('[data-expansion-empty]');
      this.expansionCount = this.querySelector('[data-expansion-count]');
      this.commandBusNotice = this.querySelector('[data-command-bus-notice]');

      this.controllerSummary = this.querySelector('[data-controller-summary]');
      this.controllerName = this.querySelector('[data-controller-name]');
      this.controllerIncluded = this.querySelector('[data-controller-included]');
      this.controllerPower = this.querySelector('[data-controller-power]');
      this.capacityList = this.querySelector('[data-capacity-list]');
      this.catalogCount = this.querySelector('[data-catalog-count]');

      this.summaryController = this.querySelector('[data-summary-controller]');
      this.summaryList = this.querySelector('[data-summary-list]');
      this.summaryEmpty = this.querySelector('[data-summary-empty]');
      this.meterList = this.querySelector('[data-meter-list]');
      this.subtotalValue = this.querySelector('[data-subtotal]');
      this.itemCountValue = this.querySelector('[data-item-count]');
      this.statusNode = this.querySelector('[data-status]');
      this.warningNode = this.querySelector('[data-warning]');

      this.addButton = this.querySelector('[data-add-cart]');
      this.addLabel = this.querySelector('[data-add-cart-label]');
      this.checkoutButton = this.querySelector('[data-checkout]');
      this.wishlistButton = this.querySelector('[data-wishlist]');

      this.dock = this.querySelector('[data-dock]');
      this.dockCount = this.querySelector('[data-dock-count]');
      this.dockTotal = this.querySelector('[data-dock-total]');
    }

    /* ---------------------------------------------------------------- state */

    restore() {
      let saved;
      try {
        saved = JSON.parse(window.sessionStorage.getItem(this.storageKey) || 'null');
      } catch (error) {
        saved = null;
      }
      if (!saved || typeof saved !== 'object') return;

      if (saved.controller && this.config.controllers[saved.controller]) {
        this.controllerKey = saved.controller;
      }
      if (Array.isArray(saved.expansions)) {
        saved.expansions.forEach((item) => {
          if (!item || !item.id) return;
          const profile = this.expansionProfile(item.id);
          if (!profile || !profile.available || profile.maxQuantity < 1) return;
          this.expansions.set(item.id, {
            id: item.id,
            quantity: Math.min(
              Math.max(Number.parseInt(item.quantity, 10) || 1, 1),
              profile.maxQuantity
            ),
          });
        });
      }
      if (!Array.isArray(saved.items)) {
        this.normalizeExpansionQuantities();
        this.persist();
        return;
      }

      saved.items.forEach((item) => {
        if (!item || !item.handle || !item.variantId) return;
        const card = this.cardFor(item.handle);
        if (!card) {
          const expansionId = this.expansionIdForHandle(item.handle);
          const profile = expansionId ? this.expansionProfile(expansionId) : null;
          if (!profile || !profile.available) return;
          this.expansions.set(expansionId, {
            id: expansionId,
            quantity: Math.min(
              Math.max(Number.parseInt(item.quantity, 10) || 1, 1),
              profile.maxQuantity
            ),
          });
          return;
        }
        const savedId = String(item.variantId);
        const select = card.querySelector('[data-variant]');
        let variant;

        if (select) {
          const option = Array.from(select.options).find(
            (candidate) => candidate.value === savedId && candidate.dataset.available === 'true'
          );
          if (!option) return;
          select.value = option.value;
          variant = this.variantFor(card);
        } else {
          variant = this.variantFor(card);
          if (!variant || String(variant.id) !== savedId || !variant.available) return;
        }
        if (!variant || !variant.available) return;

        this.selections.set(item.handle, {
          handle: item.handle,
          variantId: savedId,
          quantity: Math.min(
            Math.max(Number.parseInt(item.quantity, 10) || 1, 1),
            variant.maxQuantity
          ),
        });
      });
      this.normalizeExpansionQuantities();
      if (this.controllerKey) this.pruneInvalidSelections();
      this.persist();
    }

    persist() {
      try {
        window.sessionStorage.setItem(
          this.storageKey,
          JSON.stringify({
            controller: this.controllerKey,
            expansions: Array.from(this.expansions.values()),
            items: Array.from(this.selections.values()),
          })
        );
      } catch (error) {
        /* selection still works for this page view */
      }
    }

    cardFor(handle) {
      return this.accessoryCards.find((card) => card.dataset.handle === handle) || null;
    }

    expansionOption(id) {
      if (!this.expansionPicker || !id) return null;
      return Array.from(this.expansionPicker.options).find((option) => option.value === id) || null;
    }

    expansionIdForHandle(handle) {
      if (!this.expansionPicker || !handle) return null;
      return (
        Array.from(this.expansionPicker.options).find((option) => option.dataset.handle === handle)
          ?.value || null
      );
    }

    expansionProfile(id) {
      const option = this.expansionOption(id);
      if (!option || !option.value) return null;

      const kind = option.dataset.expansionKind;
      const controllerKey = option.dataset.controllerKey || '';
      const controller = kind === 'controller' ? this.config.controllers[controllerKey] : null;
      const accessory =
        kind === 'module' ? this.config.accessories[option.dataset.handle || ''] : null;
      if ((kind === 'controller' && !controller) || (kind === 'module' && !accessory)) return null;

      return {
        id,
        kind,
        controllerKey,
        handle: option.dataset.handle || controller?.handle || '',
        title: option.dataset.title || controller?.title || '',
        variantId: option.dataset.variantId || controller?.variantId || '',
        price: Number.parseInt(option.dataset.price || controller?.price || '0', 10),
        available: option.dataset.available === 'true' && controller?.available !== false,
        maxQuantity: Math.max(
          Number.parseInt(option.dataset.maxQuantity || controller?.maxQuantity || '20', 10),
          0
        ),
        capacity: kind === 'controller' ? controller.capacity || {} : accessory.addsCapacity || {},
        reserved: kind === 'controller' ? controller.reserved || {} : {},
        recommended: kind === 'controller' ? controller.recommended || [] : [],
      };
    }

    activeControllerKeys(controllerKey = this.controllerKey, expansions = this.expansions) {
      const keys = new Set(controllerKey ? [controllerKey] : []);
      expansions.forEach((selection, id) => {
        const profile = this.expansionProfile(id);
        if (profile?.kind === 'controller' && profile.controllerKey) {
          keys.add(profile.controllerKey);
        }
      });
      return keys;
    }

    hasSelectedProduct(handle, selections = this.selections, expansions = this.expansions) {
      if (selections.has(handle) || this.controllerProfile()?.handle === handle) return true;
      return Array.from(expansions.keys()).some(
        (id) => this.expansionProfile(id)?.handle === handle
      );
    }

    expansionLimit(profile) {
      if (!profile) return 0;
      const usedByPrimary =
        this.controllerProfile()?.variantId &&
        String(this.controllerProfile().variantId) === String(profile.variantId)
          ? 1
          : 0;
      return Math.max(profile.maxQuantity - usedByPrimary, 0);
    }

    normalizeExpansionQuantities() {
      this.expansions.forEach((selection, id) => {
        const profile = this.expansionProfile(id);
        const limit = this.expansionLimit(profile);
        if (!profile || !profile.available || limit < 1) {
          this.expansions.delete(id);
          return;
        }
        selection.quantity = Math.min(Math.max(selection.quantity, 1), limit);
      });
    }

    controllerProfile() {
      return this.controllerKey ? this.config.controllers[this.controllerKey] : null;
    }

    fits(
      handle,
      controllerKey = this.controllerKey,
      selections = this.selections,
      expansions = this.expansions
    ) {
      const accessory = this.config.accessories[handle];
      if (!accessory || !controllerKey) return false;

      const controllerKeys = this.activeControllerKeys(controllerKey, expansions);
      const directFit =
        Array.isArray(accessory.fit) && accessory.fit.some((key) => controllerKeys.has(key));
      const anyDependency =
        Array.isArray(accessory.requiresAny) &&
        accessory.requiresAny.some((required) =>
          this.hasSelectedProduct(required, selections, expansions)
        );
      const allDependencies =
        !Array.isArray(accessory.requiresAll) ||
        accessory.requiresAll.every((required) =>
          this.hasSelectedProduct(required, selections, expansions)
        );

      return (directFit || anyDependency) && allDependencies;
    }

    pruneInvalidSelections(controllerKey = this.controllerKey) {
      let changed = true;
      while (changed) {
        changed = false;
        for (const handle of Array.from(this.selections.keys())) {
          if (!this.fits(handle, controllerKey, this.selections)) {
            this.selections.delete(handle);
            changed = true;
          }
        }
      }
    }

    removeSelection(handle) {
      this.selections.delete(handle);
      this.pruneInvalidSelections();
      this.persist();
      this.render();
    }

    variantFor(card) {
      const select = card.querySelector('[data-variant]');
      if (select) {
        const option = select.options[select.selectedIndex];
        if (!option) return null;
        return {
          id: option.value,
          price: Number.parseInt(option.dataset.price || '0', 10),
          title: option.dataset.title || '',
          available: option.dataset.available === 'true',
          maxQuantity: Math.max(Number.parseInt(option.dataset.maxQuantity || '20', 10), 0),
        };
      }
      return {
        id: card.dataset.variantId,
        price: Number.parseInt(card.dataset.price || '0', 10),
        title: '',
        available: card.dataset.available === 'true',
        maxQuantity: Math.max(Number.parseInt(card.dataset.maxQuantity || '20', 10), 0),
      };
    }

    lineItems() {
      const items = [];
      const profile = this.controllerProfile();
      if (profile) {
        items.push({
          handle: profile.handle,
          title: profile.title,
          variantTitle: '',
          variantId: profile.variantId,
          price: profile.price,
          quantity: 1,
          isController: true,
          isExpansion: false,
        });
      }

      this.expansions.forEach((selection, id) => {
        const expansion = this.expansionProfile(id);
        const limit = this.expansionLimit(expansion);
        if (!expansion || !expansion.variantId || !expansion.available || limit < 1) return;

        items.push({
          stateId: id,
          kind: expansion.kind,
          handle: expansion.handle,
          title: expansion.title,
          variantTitle: '',
          variantId: String(expansion.variantId),
          price: expansion.price,
          quantity: Math.min(selection.quantity, limit),
          maxQuantity: limit,
          ports: {},
          addsCapacity: expansion.capacity,
          addsReserved: expansion.reserved,
          replacesReserved: {},
          isController: false,
          isExpansion: true,
        });
      });

      this.selections.forEach((selection) => {
        const card = this.cardFor(selection.handle);
        if (!card || !this.fits(selection.handle)) return;
        const option = Array.from(card.querySelectorAll('[data-variant] option')).find(
          (candidate) => candidate.value === selection.variantId
        );
        const variant = option
          ? {
              id: option.value,
              price: Number.parseInt(option.dataset.price || '0', 10),
              title: option.dataset.title || '',
              available: option.dataset.available === 'true',
              maxQuantity: Math.max(Number.parseInt(option.dataset.maxQuantity || '20', 10), 0),
            }
          : this.variantFor(card);
        if (!variant || !variant.id || !variant.available || variant.maxQuantity < 1) return;

        items.push({
          handle: selection.handle,
          title: card.dataset.title || '',
          variantTitle: variant.title || '',
          variantId: String(variant.id),
          price: variant.price,
          quantity: Math.min(selection.quantity, variant.maxQuantity),
          ports: this.config.accessories[selection.handle]?.ports || {},
          addsCapacity: this.config.accessories[selection.handle]?.addsCapacity || {},
          replacesReserved: this.config.accessories[selection.handle]?.replacesReserved || {},
          isController: false,
          isExpansion: false,
        });
      });

      return items;
    }

    /* -------------------------------------------------------------- actions */

    onClick = (event) => {
      const controllerCard = event.target.closest('[data-select-controller]');
      if (controllerCard && this.contains(controllerCard)) {
        this.selectController(controllerCard.closest('[data-controller-card]').dataset.key);
        return;
      }

      const toggle = event.target.closest('[data-toggle-accessory]');
      if (toggle && this.contains(toggle)) {
        this.toggleAccessory(toggle.closest('[data-accessory]'));
        return;
      }

      if (event.target.closest('[data-add-expansion]')) {
        this.addExpansion();
        return;
      }

      const removeExpansion = event.target.closest('[data-remove-expansion]');
      if (removeExpansion && this.contains(removeExpansion)) {
        this.removeExpansion(removeExpansion.dataset.removeExpansion);
        return;
      }

      const expansionStep = event.target.closest('[data-expansion-step]');
      if (expansionStep && this.contains(expansionStep)) {
        this.changeExpansionQuantity(
          expansionStep.dataset.expansionId,
          Number.parseInt(expansionStep.dataset.expansionStep, 10)
        );
        return;
      }

      const remove = event.target.closest('[data-remove]');
      if (remove && this.contains(remove)) {
        this.removeSelection(remove.dataset.remove);
        return;
      }

      const step = event.target.closest('[data-step]');
      if (step && this.contains(step)) {
        this.changeQuantity(step.dataset.handle, Number.parseInt(step.dataset.step, 10));
        return;
      }

      const filter = event.target.closest('[data-filter]');
      if (filter && this.contains(filter)) {
        this.setFilter(filter.dataset.filter);
        return;
      }

      const dockReview = event.target.closest('[data-dock-review]');
      if (dockReview && this.contains(dockReview)) {
        const summary = this.querySelector('[data-summary]');
        const heading = this.querySelector('[data-summary-heading]');
        summary?.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'start',
        });
        heading?.focus({ preventScroll: true });
        return;
      }

      if (event.target.closest('[data-add-cart]')) {
        this.submit({ checkout: false });
        return;
      }
      if (event.target.closest('[data-checkout]')) {
        this.submit({ checkout: true });
        return;
      }
      if (event.target.closest('[data-wishlist]')) {
        this.saveToWishlist();
        return;
      }

      if (event.target.closest('a, button, select, option, input, label, textarea')) return;

      const wholeControllerCard = event.target.closest('[data-controller-card]');
      if (wholeControllerCard && this.contains(wholeControllerCard)) {
        wholeControllerCard.querySelector('[data-select-controller]')?.click();
        return;
      }

      const wholeAccessoryCard = event.target.closest('[data-accessory]');
      if (wholeAccessoryCard && this.contains(wholeAccessoryCard)) {
        wholeAccessoryCard.querySelector('[data-toggle-accessory]')?.click();
      }
    };

    onChange = (event) => {
      if (event.target.matches('[data-expansion-picker]')) {
        this.renderExpansionPicker();
        return;
      }

      if (event.target.matches('[data-variant]')) {
        const card = event.target.closest('[data-accessory]');
        const handle = card.dataset.handle;
        if (this.selections.has(handle)) {
          const variant = this.variantFor(card);
          if (variant && variant.available) {
            this.selections.get(handle).variantId = String(variant.id);
          } else {
            this.selections.delete(handle);
          }
          this.persist();
        }
        this.render();
        return;
      }

      if (event.target.matches('[data-recommended-toggle]')) {
        this.renderCatalog();
      }
    };

    onKeydown = (event) => {
      const button = event.target.closest('[data-select-controller]');
      if (!button || !this.contains(button)) return;

      const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
      if (!keys.includes(event.key)) return;
      event.preventDefault();

      const enabled = this.controllerCards
        .map((card) => card.querySelector('[data-select-controller]'))
        .filter((candidate) => candidate && !candidate.disabled);
      const current = enabled.indexOf(button);
      let next = current;
      if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = enabled.length - 1;
      else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        next = (current + 1) % enabled.length;
      } else {
        next = (current - 1 + enabled.length) % enabled.length;
      }
      enabled[next]?.focus();
      enabled[next]?.click();
    };

    selectController(key, { silent = false } = {}) {
      if (!key || !this.config.controllers[key]) return;

      if (!silent && key !== this.controllerKey) {
        const retained = new Map(this.selections);
        let changed = true;
        while (changed) {
          changed = false;
          for (const handle of Array.from(retained.keys())) {
            if (!this.fits(handle, key, retained)) {
              retained.delete(handle);
              changed = true;
            }
          }
        }
        const dropped = Array.from(this.selections.keys()).filter((handle) => !retained.has(handle));
        if (dropped.length) {
          const names = dropped
            .map((handle) => this.cardFor(handle)?.dataset.title || handle)
            .join(', ');
          const confirmed = window.confirm(
            `${dropped.length} selected ${
              dropped.length === 1 ? 'item is' : 'items are'
            } not compatible with the ${this.config.controllers[key].title}:\n\n${names}\n\nRemove ${
              dropped.length === 1 ? 'it' : 'them'
            } and continue?`
          );
          if (!confirmed) return;
          this.selections = retained;
        }
      }

      this.controllerKey = key;
      this.normalizeExpansionQuantities();
      this.pruneInvalidSelections();
      this.clearStatus();
      this.persist();
      this.render();
    }

    addExpansion() {
      const id = this.expansionPicker?.value;
      const profile = this.expansionProfile(id);
      const limit = this.expansionLimit(profile);
      if (!id || !profile || !profile.available || limit < 1) return;

      const existing = this.expansions.get(id);
      if (existing) {
        existing.quantity = Math.min(existing.quantity + 1, limit);
      } else {
        this.expansions.set(id, { id, quantity: 1 });
      }

      this.expansionPicker.value = '';
      this.clearStatus();
      this.persist();
      this.render();
    }

    removeExpansion(id) {
      if (!id) return;
      this.expansions.delete(id);
      this.pruneInvalidSelections();
      this.clearStatus();
      this.persist();
      this.render();
    }

    changeExpansionQuantity(id, delta) {
      const selection = this.expansions.get(id);
      const profile = this.expansionProfile(id);
      if (!selection || !profile || !Number.isFinite(delta)) return;

      const next = selection.quantity + delta;
      if (next < 1) {
        this.expansions.delete(id);
        this.pruneInvalidSelections();
      } else {
        selection.quantity = Math.min(next, this.expansionLimit(profile));
      }
      this.clearStatus();
      this.persist();
      this.render();
    }

    toggleAccessory(card) {
      if (!card) return;
      const handle = card.dataset.handle;
      if (!this.fits(handle)) return;

      if (this.selections.has(handle)) {
        this.selections.delete(handle);
        this.pruneInvalidSelections();
      } else {
        const variant = this.variantFor(card);
        if (!variant || !variant.id || !variant.available) return;
        this.selections.set(handle, { handle, variantId: String(variant.id), quantity: 1 });
      }
      this.clearStatus();
      this.persist();
      this.render();
    }

    changeQuantity(handle, delta) {
      const selection = this.selections.get(handle);
      if (!selection || !Number.isFinite(delta)) return;
      const next = selection.quantity + delta;
      if (next < 1) {
        this.selections.delete(handle);
        this.pruneInvalidSelections();
      } else {
        const card = this.cardFor(handle);
        const limit = card ? this.variantFor(card)?.maxQuantity || 20 : 20;
        selection.quantity = Math.min(next, limit);
      }
      this.persist();
      this.render();
    }

    setFilter(group) {
      this.filterButtons.forEach((button) => {
        const active = button.dataset.filter === group;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      this.activeFilter = group;
      this.renderCatalog();
    }

    /* ------------------------------------------------------------ rendering */

    render() {
      this.renderControllers();
      this.renderExpansions();
      this.renderCatalog();
      this.renderSummary();
    }

    renderControllers() {
      const profile = this.controllerProfile();

      this.controllerCards.forEach((card) => {
        const active = card.dataset.key === this.controllerKey;
        card.classList.toggle('is-selected', active);
        const available = this.config.controllers[card.dataset.key]?.available !== false;
        card.classList.toggle('is-unavailable', !available);
        const button = card.querySelector('[data-select-controller]');
        if (button) {
          button.disabled = !available;
          button.setAttribute('aria-checked', String(active));
          button.tabIndex = active ? 0 : -1;
          const label = button.querySelector('[data-select-label]');
          if (label) label.textContent = !available ? 'Sold out' : active ? 'Selected' : 'Select';
        }
      });

      if (!profile) return;

      const switched = this.lastControllerKey !== null && this.lastControllerKey !== this.controllerKey;
      this.lastControllerKey = this.controllerKey;
      if (switched) {
        [this.controllerName, this.controllerIncluded, this.controllerPower].forEach((node, index) => {
          node?.style.setProperty('--tshbc-i', String(index));
          this.replayEntrance(node);
        });
      }

      if (this.controllerName) this.controllerName.textContent = profile.title;
      if (this.controllerIncluded) {
        this.controllerIncluded.textContent = profile.included.length
          ? `In the box: ${profile.included.join(', ')}.`
          : '';
      }
      if (this.controllerPower) this.controllerPower.textContent = profile.powerNote || '';

      if (this.capacityList) {
        const chips = PORT_ORDER.filter((type) => (profile.capacity[type] || 0) > 0).map((type, index) => {
          const total = profile.capacity[type];
          const reserved = profile.reserved[type] || 0;
          const free = total - reserved;
          const li = document.createElement('li');
          li.className = 'tshbc-chip';
          if (switched) {
            li.classList.add('is-entering');
            li.style.setProperty('--tshbc-i', String(index + 2));
          }
          li.innerHTML = `<b>${total}</b><span>${PORT_LABELS[type]}${
            reserved ? ` · ${free} free, ${reserved} included in use` : ''
          }</span>`;
          return li;
        });
        this.capacityList.replaceChildren(...chips);
      }
    }

    renderExpansionPicker() {
      if (!this.expansionAddButton) return;
      const id = this.expansionPicker?.value;
      const profile = this.expansionProfile(id);
      const selectedQuantity = this.expansions.get(id)?.quantity || 0;
      this.expansionAddButton.disabled =
        !profile ||
        !profile.available ||
        this.expansionLimit(profile) < 1 ||
        selectedQuantity >= this.expansionLimit(profile);
    }

    renderExpansions() {
      const items = Array.from(this.expansions.entries())
        .map(([id, selection]) => {
          const profile = this.expansionProfile(id);
          return profile
            ? { ...profile, quantity: selection.quantity, limit: this.expansionLimit(profile) }
            : null;
        })
        .filter(Boolean);
      const previousExpansionKeys = this.renderedExpansionKeys;
      this.renderedExpansionKeys = new Set(items.map((item) => item.id));

      if (this.expansionList) {
        this.expansionList.replaceChildren(
          ...items.map((item) => {
            const li = document.createElement('li');
            li.className = 'tshbc-line tshbc-line--expansion';
            if (previousExpansionKeys && !previousExpansionKeys.has(item.id)) li.classList.add('is-entering');
            li.innerHTML = `
              <div class="tshbc-line__main">
                <span class="tshbc-line__eyebrow"></span>
                <span class="tshbc-line__title"></span>
                <span class="tshbc-line__variant"></span>
                <div class="tshbc-qty">
                  <button type="button" data-expansion-step="-1">−</button>
                  <span data-qty></span>
                  <button type="button" data-expansion-step="1">+</button>
                </div>
              </div>
              <div class="tshbc-line__side">
                <span class="tshbc-line__price"></span>
                <button type="button" class="tshbc-line__remove" data-remove-expansion>Remove</button>
              </div>`;

            li.querySelector('.tshbc-line__eyebrow').textContent =
              item.kind === 'module' ? 'Power / output module' : 'Additional controller';
            li.querySelector('.tshbc-line__title').textContent = item.title;
            const capacity = Object.entries(item.capacity)
              .filter(([, amount]) => amount > 0)
              .map(([type, amount]) => `+${amount} ${PORT_LABELS[type] || type}`)
              .join(' · ');
            const capacityNode = li.querySelector('.tshbc-line__variant');
            capacityNode.textContent = capacity;
            capacityNode.hidden = !capacity;
            li.querySelector('[data-qty]').textContent = String(item.quantity);
            li.querySelector('.tshbc-line__price').textContent = this.money.format(
              (item.price * item.quantity) / 100
            );
            li.querySelectorAll('[data-expansion-step]').forEach((button) => {
              button.dataset.expansionId = item.id;
              if (button.dataset.expansionStep === '1') {
                button.disabled = item.quantity >= item.limit;
              }
              button.setAttribute(
                'aria-label',
                `${button.dataset.expansionStep === '-1' ? 'Decrease' : 'Increase'} quantity for ${
                  item.title
                }`
              );
            });
            const remove = li.querySelector('[data-remove-expansion]');
            remove.dataset.removeExpansion = item.id;
            remove.setAttribute('aria-label', `Remove ${item.title}`);
            return li;
          })
        );
      }

      const count = items.reduce((total, item) => total + item.quantity, 0);
      if (this.expansionEmpty) this.expansionEmpty.hidden = count > 0;
      if (this.commandBusNotice) this.commandBusNotice.hidden = count === 0;
      if (this.expansionCount) {
        this.expansionCount.textContent = `${count} added`;
      }
      this.renderExpansionPicker();
    }

    systemReserved(profile = this.controllerProfile()) {
      const reserved = { ...(profile?.reserved || {}) };
      this.expansions.forEach((selection, id) => {
        const expansion = this.expansionProfile(id);
        Object.entries(expansion?.reserved || {}).forEach(([type, amount]) => {
          reserved[type] = (reserved[type] || 0) + amount * selection.quantity;
        });
      });
      return reserved;
    }

    renderCatalog() {
      const recommendedOnly = Boolean(this.recommendedToggle?.checked);
      const profile = this.controllerProfile();
      const recommended = new Set(profile ? profile.recommended : []);
      this.expansions.forEach((selection, id) => {
        const expansion = this.expansionProfile(id);
        (expansion?.recommended || []).forEach((handle) => recommended.add(handle));
      });
      const reserved = this.systemReserved(profile);
      const enteringByGrid = new Map();
      let visible = 0;

      this.accessoryCards.forEach((card) => {
        const handle = card.dataset.handle;
        const accessory = this.config.accessories[handle];
        const compatible = this.fits(handle);
        const isRecommended = recommended.has(handle);
        const groupMatch = !this.activeFilter || this.activeFilter === 'all' || accessory.group === this.activeFilter;
        const show = compatible && groupMatch && (!recommendedOnly || isRecommended);

        if (show && card.hidden) {
          const order = enteringByGrid.get(card.parentElement) || 0;
          enteringByGrid.set(card.parentElement, order + 1);
          card.style.setProperty('--tshbc-i', String(order));
        }
        card.hidden = !show;
        if (show) visible += 1;

        card.classList.toggle('is-selected', this.selections.has(handle));
        card.classList.toggle('is-recommended', isRecommended);

        const badge = card.querySelector('[data-recommended-badge]');
        if (badge) badge.hidden = !isRecommended;

        const note = card.querySelector('[data-note]');
        if (note) note.textContent = accessory.note || '';

        const variant = this.variantFor(card);
        const price = card.querySelector('[data-price-display]');
        if (price && variant) {
          price.textContent = variant.available ? this.money.format(variant.price / 100) : 'Out of stock';
        }

        const button = card.querySelector('[data-toggle-accessory]');
        if (button && variant) {
          const selected = this.selections.has(handle);
          const replacesIncluded = Object.entries(accessory.replacesReserved || {}).some(
            ([type, amount]) => amount > 0 && (reserved[type] || 0) > 0
          );
          button.disabled = !variant.available;
          button.setAttribute('aria-pressed', String(selected));
          const label = button.querySelector('[data-add-label]');
          if (label) {
            label.textContent = !variant.available
              ? 'Out of stock'
              : selected
                ? 'Added'
                : replacesIncluded
                  ? 'Replace'
                  : 'Add';
          }
        }
      });

      this.groupSections.forEach((section) => {
        const hasVisible = Array.from(section.querySelectorAll('[data-accessory]')).some(
          (card) => !card.hidden
        );
        section.hidden = !hasVisible;
      });

      if (this.catalogCount) {
        this.catalogCount.textContent = visible
          ? `${visible} compatible ${visible === 1 ? 'item' : 'items'}`
          : 'No matching items';
      }
    }

    renderSummary() {
      const items = this.lineItems();
      const addOns = items.filter((item) => !item.isController);
      const subtotal = items.reduce((total, item) => total + item.price * item.quantity, 0);
      const count = items.reduce((total, item) => total + item.quantity, 0);
      const profile = this.controllerProfile();
      const lineKey = (item) => item.stateId || item.handle;
      const previousLineKeys = this.renderedLineKeys;
      this.renderedLineKeys = new Set(items.map(lineKey));

      if (this.summaryController) {
        this.summaryController.innerHTML = '';
        if (profile) {
          const row = document.createElement('div');
          row.className = 'tshbc-line tshbc-line--controller';
          if (previousLineKeys && !previousLineKeys.has(profile.handle)) row.classList.add('is-entering');
          row.innerHTML = `
            <div class="tshbc-line__main">
              <span class="tshbc-line__eyebrow">Primary controller</span>
              <span class="tshbc-line__title"></span>
            </div>
            <span class="tshbc-line__price"></span>`;
          row.querySelector('.tshbc-line__title').textContent = profile.title;
          row.querySelector('.tshbc-line__price').textContent = this.money.format(profile.price / 100);
          this.summaryController.append(row);
        }
      }

      if (this.summaryList) {
        this.summaryList.replaceChildren(
          ...addOns.map((item) => {
            const li = document.createElement('li');
            li.className = 'tshbc-line';
            if (previousLineKeys && !previousLineKeys.has(lineKey(item))) li.classList.add('is-entering');
            li.innerHTML = `
              <div class="tshbc-line__main">
                <span class="tshbc-line__eyebrow"></span>
                <span class="tshbc-line__title"></span>
                <span class="tshbc-line__variant"></span>
                <div class="tshbc-qty">
                  <button type="button" aria-label="Decrease quantity">−</button>
                  <span data-qty></span>
                  <button type="button" aria-label="Increase quantity">+</button>
                </div>
              </div>
              <div class="tshbc-line__side">
                <span class="tshbc-line__price"></span>
                <button type="button" class="tshbc-line__remove" aria-label="Remove">Remove</button>
              </div>`;
            const eyebrow = li.querySelector('.tshbc-line__eyebrow');
            eyebrow.textContent = item.isExpansion
              ? item.kind === 'module'
                ? 'Power / output module'
                : 'Additional controller'
              : '';
            eyebrow.hidden = !item.isExpansion;
            li.querySelector('.tshbc-line__title').textContent = item.title;
            const variantNode = li.querySelector('.tshbc-line__variant');
            variantNode.textContent = item.variantTitle;
            variantNode.hidden = !item.variantTitle;
            li.querySelector('[data-qty]').textContent = String(item.quantity);
            li.querySelector('.tshbc-line__price').textContent = this.money.format(
              (item.price * item.quantity) / 100
            );
            li.querySelectorAll('.tshbc-qty button').forEach((button, index) => {
              const delta = index === 0 ? '-1' : '1';
              if (item.isExpansion) {
                button.dataset.expansionStep = delta;
                button.dataset.expansionId = item.stateId;
                if (delta === '1') button.disabled = item.quantity >= item.maxQuantity;
              } else {
                button.dataset.step = delta;
                button.dataset.handle = item.handle;
              }
              button.setAttribute(
                'aria-label',
                `${delta === '-1' ? 'Decrease' : 'Increase'} quantity for ${item.title}`
              );
            });
            const remove = li.querySelector('.tshbc-line__remove');
            if (item.isExpansion) {
              remove.dataset.removeExpansion = item.stateId;
            } else {
              remove.dataset.remove = item.handle;
            }
            remove.setAttribute('aria-label', `Remove ${item.title}`);
            return li;
          })
        );
      }

      if (this.summaryEmpty) this.summaryEmpty.hidden = addOns.length > 0;
      if (this.subtotalValue) this.subtotalValue.textContent = this.money.format(subtotal / 100);
      if (this.itemCountValue) {
        this.itemCountValue.textContent = `${count} ${count === 1 ? 'item' : 'items'}`;
      }

      this.renderMeters(profile, addOns);

      if (profile && profile.available === false && this.warningNode) {
        this.warningNode.textContent = `The ${profile.title} is currently sold out, so this build cannot be added to the cart yet. You can still save it to your wishlist.`;
        this.warningNode.hidden = false;
      }

      const soldOut = Boolean(profile) && profile.available === false;
      const disabled = this.busy || !profile || soldOut || this.hasPortOverload;
      if (this.addButton) this.addButton.disabled = disabled;
      if (this.checkoutButton) this.checkoutButton.disabled = disabled;
      if (this.wishlistButton) this.wishlistButton.disabled = this.busy || !profile;
      if (this.addLabel && !this.busy) {
        this.addLabel.textContent = count > 1 ? `Add ${count} items to cart` : 'Add build to cart';
      }

      this.updateDockVisibility();
      if (this.dockCount) this.dockCount.textContent = `${count} ${count === 1 ? 'item' : 'items'}`;
      if (this.dockTotal) this.dockTotal.textContent = this.money.format(subtotal / 100);
    }

    renderMeters(profile, accessories) {
      if (!this.meterList) return;
      this.hasPortOverload = false;
      if (!profile) {
        this.meterList.replaceChildren();
        return;
      }

      const used = {};
      const added = {};
      const addedReserved = {};
      const replacements = {};
      accessories.forEach((item) => {
        Object.entries(item.ports || {}).forEach(([type, amount]) => {
          used[type] = (used[type] || 0) + amount * item.quantity;
        });
        Object.entries(item.addsCapacity || {}).forEach(([type, amount]) => {
          added[type] = (added[type] || 0) + amount * item.quantity;
        });
        Object.entries(item.addsReserved || {}).forEach(([type, amount]) => {
          addedReserved[type] = (addedReserved[type] || 0) + amount * item.quantity;
        });
        Object.entries(item.replacesReserved || {}).forEach(([type, amount]) => {
          replacements[type] = (replacements[type] || 0) + amount * item.quantity;
        });
      });

      const overloaded = [];
      const meters = PORT_ORDER.filter((type) => (used[type] || 0) > 0).map((type) => {
        const baseCapacity = profile.capacity[type] || 0;
        const reserved = (profile.reserved[type] || 0) + (addedReserved[type] || 0);
        const released = Math.min(replacements[type] || 0, reserved);
        const available = Math.max(baseCapacity + (added[type] || 0) - reserved + released, 0);
        const consumed = used[type] || 0;
        if (consumed > available) overloaded.push(PORT_LABELS[type]);

        const li = document.createElement('li');
        li.className = 'tshbc-meter';
        if (consumed > available) li.classList.add('is-over');
        li.innerHTML = `
          <div class="tshbc-meter__head">
            <span></span><b></b>
          </div>
          <div class="tshbc-meter__track"><span></span></div>`;
        li.querySelector('span').textContent = PORT_LABELS[type];
        li.querySelector('b').textContent = `${consumed} of ${available}`;
        li.querySelector('.tshbc-meter__track span').style.width = `${
          available > 0 ? Math.min((consumed / available) * 100, 100) : 100
        }%`;
        return li;
      });

      this.meterList.replaceChildren(...meters);
      this.hasPortOverload = overloaded.length > 0;

      if (this.warningNode) {
        if (overloaded.length) {
          this.warningNode.textContent = `This build needs more ${overloaded.join(
            ' and '
          )} than the selected controller and add-ons provide. Add the appropriate expansion module or remove an item.`;
          this.warningNode.hidden = false;
        } else {
          this.warningNode.hidden = true;
        }
      }
    }

    updateDockVisibility() {
      if (!this.dock) return;
      this.dock.hidden = !this.controllerProfile() || !this.inViewport;
    }

    /* ---------------------------------------------------------------- submit */

    async submit({ checkout }) {
      if (this.busy) return;
      const items = this.lineItems();
      if (!items.length) return;

      this.clearStatus();
      this.setBusy(true, checkout ? 'Starting checkout…' : 'Adding…');

      let result = {};
      try {
        const buildId = this.buildId();
        const cartItems = new Map();
        items.forEach((item) => {
          const id = Number(String(item.variantId).split('/').pop());
          cartItems.set(id, (cartItems.get(id) || 0) + item.quantity);
        });
        const payload = {
          items: Array.from(cartItems, ([id, quantity]) => ({
            id,
            quantity,
            properties: {
              _hydros_build_id: buildId,
              _hydros_build_controller: this.controllerProfile()?.title || '',
            },
          })),
        };

        const sections = this.cartSectionIds();
        if (sections.length && !checkout) {
          payload.sections = sections.join(',');
          payload.sections_url = window.location.pathname;
        }

        const response = await fetch(this.config.routes.cartAdd, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(payload),
        });
        result = await this.parseJson(response);

        if (!response.ok || result.status) {
          throw new Error(result.description || result.message || 'This build could not be added.');
        }

        if (checkout) {
          window.location.href = '/checkout';
          return;
        }

        const cart = await this.refreshCart(result.sections || {});
        if (!cart) {
          this.setStatus(
            'Your build was added, but the cart display could not refresh. Open the cart to review it.',
            true
          );
          return;
        }

        const units = items.reduce((total, item) => total + item.quantity, 0);
        this.setStatus(`Added ${units} ${units === 1 ? 'item' : 'items'} to your cart.`);
      } catch (error) {
        // Shopify can accept the available portion of an over-stock request before
        // returning an error. Reconcile the cart so the drawer and item count never
        // disagree with what actually landed.
        await this.refreshCart(result.sections || {});
        this.setStatus(error instanceof Error ? error.message : 'This build could not be added.', true);
      } finally {
        this.setBusy(false);
      }
    }

    async saveToWishlist() {
      const wishlist = this.config.wishlist;
      if (!wishlist || !wishlist.customerId) {
        window.location.href = `${this.config.routes.login}?return_url=${encodeURIComponent(
          window.location.pathname
        )}`;
        return;
      }
      if (this.busy) return;

      const items = Array.from(
        new Map(this.lineItems().map((item) => [String(item.variantId), item])).values()
      );
      if (!items.length) return;

      this.clearStatus();
      this.setBusy(true, 'Saving…');

      try {
        let saved = 0;
        const customerId = this.toGid('Customer', wishlist.customerId);

        // Keep concurrency bounded so a large build does not flood the wishlist
        // service, while avoiding a slow one-request-at-a-time save.
        for (let index = 0; index < items.length; index += 4) {
          const batch = items.slice(index, index + 4);
          const outcomes = await Promise.allSettled(
            batch.map(async (item) => {
              const response = await fetch(`${wishlist.endpoint}/items`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-Wishlist-Token': wishlist.token },
                body: JSON.stringify({
                  customer: { id: customerId, email: wishlist.customerEmail },
                  variant_id: this.toGid('ProductVariant', item.variantId),
                }),
              });
              const data = await this.parseJson(response);
              return response.ok && data?.ok !== false;
            })
          );
          saved += outcomes.filter(
            (outcome) => outcome.status === 'fulfilled' && outcome.value === true
          ).length;
        }

        this.setStatus(
          saved === items.length
            ? `Saved ${saved} ${saved === 1 ? 'product' : 'products'} to your wishlist.`
            : `Saved ${saved} of ${items.length} products. Please try the rest again.`,
          saved !== items.length
        );
      } catch (error) {
        this.setStatus('Your wishlist could not be updated. Please try again.', true);
      } finally {
        this.setBusy(false);
      }
    }

    async parseJson(response) {
      const text = await response.text();
      try {
        return JSON.parse(text || '{}');
      } catch (error) {
        return { status: response.status, message: response.statusText };
      }
    }

    async refreshCart(sections = {}) {
      try {
        const response = await fetch(this.config.routes.cart, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return null;
        const cart = await this.parseJson(response);
        if (!Number.isFinite(cart.item_count)) return null;

        this.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: cart,
              sourceId: this.id,
              data: {
                source: 'topshelf-hydros-configurator',
                itemCount: cart.item_count,
                sections,
              },
            },
          })
        );
        return cart;
      } catch (error) {
        return null;
      }
    }

    cartSectionIds() {
      const ids = new Set();
      document.querySelectorAll('cart-items-component[data-section-id]').forEach((node) => {
        if (node.dataset.sectionId) ids.add(node.dataset.sectionId);
      });
      return Array.from(ids);
    }

    buildId() {
      if (window.crypto?.randomUUID) return window.crypto.randomUUID();
      return `hydros-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    }

    toGid(type, id) {
      const value = String(id || '');
      return value.startsWith(`gid://shopify/${type}/`) ? value : `gid://shopify/${type}/${value}`;
    }

    setBusy(busy, label) {
      this.busy = busy;
      this.toggleAttribute('aria-busy', busy);
      if (busy && label && this.addLabel) this.addLabel.textContent = label;
      this.renderSummary();
    }

    setStatus(message, isError = false) {
      if (!this.statusNode) return;
      this.statusNode.textContent = message;
      this.statusNode.dataset.error = String(isError);
      this.statusNode.hidden = false;
    }

    clearStatus() {
      if (!this.statusNode) return;
      this.statusNode.textContent = '';
      this.statusNode.hidden = true;
      this.statusNode.removeAttribute('data-error');
    }
  }

  if (!customElements.get('topshelf-hydros-configurator')) {
    customElements.define('topshelf-hydros-configurator', TopShelfHydrosConfigurator);
  }
})();
